import React, { useContext, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'

import { useTheme } from '@mui/material/styles'
// material-ui
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'

import { CustomIcon, ModalContainer } from '@contenthouse/components'
import { useSetState, useThunkDispatch } from '@contenthouse/hooks'
import { digObject, getUniquePropertiesWithValues } from '@contenthouse/utilities'

// project imports
import useCurrentUser from 'hooks/useCurrentUser'
import useProject, { useProjectForm } from 'hooks/useProject'
import useReduxAction from 'hooks/useReduxAction'
import { loadUser } from 'redux/modules/user'

import ModalContext from 'contexts/modalContext'
import PartTypes from 'ui-component/PartTypes'
import CalendarInvitees from 'shared/functions/getCalendarInvitees'
import ProjectArchived from 'layout/ProjectArchived'

import config from 'config'

//state
const defaultState = {
  disableFields: false,
  errors: [],
  openArchive: false,
  ownerUser: null,
  projectUpdated: false,
  skipChecked: false,
}

const ProjectModal = ({ modalKey = 'ProjectModal', ...props }) => {
  const navigate = useNavigate()

  const [state, setState] = useSetState(defaultState)
  const { disableFields, errors, openArchive, ownerUser, projectUpdated, skipChecked } = state

  const theme = useTheme()

  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, `${modalKey}`, {})

  const { project, office } = modalPayload

  const entityReducer = useSelector((reduxState) => reduxState.projectTemplates)
  const { result: projectTemplate, loading: templateLoading } = entityReducer

  const { users } = useSelector((reduxState) => reduxState.entities)

  const projectFormPayload = useProjectForm(project)
  const {
    entityState,
    entityState: { display_name, parts, owner, collaborators },
    saveEnabled,
    setEntityState,
  } = projectFormPayload

  const {
    callbacks: { createProject: createFn, updateProject: updateFn, patchProject: patchFn },
    creating,
    updating,
  } = useProject()

  const { currentUser } = useCurrentUser()
  const dispatch = useThunkDispatch()

  const missingUserIds = useMemo(() => {
    const selectedIds = [owner, ...(collaborators || [])].filter(Boolean)
    const uniqueIds = [...new Set(selectedIds)]
    return uniqueIds.filter((id) => !users[id])
  }, [owner, collaborators, users])

  useEffect(() => {
    if (!missingUserIds.length) return
    missingUserIds.forEach((id) => dispatch(loadUser(id)))
  }, [dispatch, missingUserIds])

  const isUserInOfficeScope = (user, selectedIds = []) => {
    if (selectedIds.includes(user?.id)) return true

    if (office) {
      return user?.agent?.office === office || user?.client_admin?.offices?.includes(office) || user?.assistant?.office === office
    }

    if (project?.office) {
      return (
        user?.agent?.office === project.office ||
        user?.client_admin?.offices?.includes(project.office) ||
        user?.assistant?.office === project.office
      )
    }

    return (
      user?.agent?.office === ownerUser?.agent?.office ||
      user?.client_admin?.offices?.includes(ownerUser?.agent?.office) ||
      user?.assistant?.office === ownerUser?.agent?.office
    )
  }

  useReduxAction('projectTemplates', 'loadProjectTemplate', {}, [], {
    dispatchAction: (action, requestOptions) => action({ parts: config.defaultProjectParts }, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { errors, loading } = entityReducer
      return !loading && !errors.length && !project?.id
    },
  })

  const save = (id) => {
    if (id) {
      const otherProjectValues = getUniquePropertiesWithValues(project, entityState)
      updateFn({ ...entityState, ...otherProjectValues }).then(({ success, errors, result }) => {
        if (!success && errors) {
          toast.warn(errors[0])
          return
        }
        closeModal()
      })
    } else {
      createFn(entityState).then(({ success, errors, result }) => {
        if (!success && errors) {
          toast.warn(errors[0])
          return
        }
        closeModal()
        navigate(`/project/${result.id}`, {
          state: {
            category: project ? project?.category?.icon : '',
          },
        })
      })
    }
  }

  const archive = () => {
    patchFn({ id: project.id, archive_date: DateTime.now().toUTC().toISO() }).then(({ success, errors }) => {
      if (!success && errors) {
        toast.warn(errors[0])
        return
      }
      closeModal()
      navigate('/')
    })
  }

  const setArchive = (val) => {
    setState({ openArchive: val })
  }

  const setValue = (part, value, error) => {
    const compositeId = part.number
    const existingError = errors.some((x) => x.id === part.number)
    if (error && !existingError) {
      setState({ errors: [{ id: compositeId, error }, ...errors] })
    } else if (!error && existingError) {
      setState({ errors: errors.filter((x) => x.id != compositeId) })
    }

    if (value || value === false) {
      part.value = value

      if (part.name === 'Service Address' && !projectUpdated && value.address) {
        entityState.display_name = value.address + (value.suburb ? ', ' + value.suburb : '')
      }
    } else if (Object.hasOwn(part, 'value')) {
      delete part.value
    }

    setEntityState(entityState)
  }

  useEffect(() => {
    if (project === undefined && skipChecked) {
      setState({ disableFields: true })
    } else if ((!skipChecked && project === undefined) || project) {
      setState({ disableFields: false })
      setState({ skipChecked: false })
    }
  }, [project, skipChecked])

  useEffect(() => {
    setEntityState({
      parts: project?.id ? project?.parts || [] : projectTemplate?.parts || [],
      owner: project?.id ? project?.owner : office && !['Agent', 'Client_Admin', 'Assistant'].some(role => currentUser?.roles?.includes(role)) ? '' : currentUser.id,
      office: project?.id ? (project?.office ? project.office : project?.owner?.agent?.office) : office ? office : ownerUser?.agent?.office,
    })
  }, [projectTemplate, project?.id])

  useEffect(() => {
    if (users[owner] && users[owner].id !== ownerUser?.id) {
      setState({ ownerUser: users[owner] })
    }
  }, [users, owner])

  useEffect(() => {
    if (project?.id && display_name) {
      setState({ projectUpdated: true })
    }
  }, [project, display_name])

  const invitees = CalendarInvitees(project?.id ? project : { office: office || ownerUser?.agent?.office, owner, collaborators })

  useEffect(() => {
    if (!project?.calendar_invite_emails?.length) {
      setEntityState({ calendar_invite_emails: invitees })
    }
  }, [project])

  useEffect(() => {
    if (!project?.id) {
      setEntityState({ calendar_invite_emails: invitees })
    }
  }, [owner, collaborators])

  return (
    <ModalContainer callbacks={callbacks} modalKey={modalKey} showModal={showModal} size={project?.category || project?.archive_date ? '' : 'large'}>
      <ModalContainer.Header
        callbacks={callbacks}
        heading={project?.id ? 'Edit Project' : project?.category ? 'Quick Order' : 'New Project'}
        headingNode={
          !project?.id && project?.category ? (
            <Chip
              icon={<CustomIcon name={project?.category?.icon} width={23} height={23} marginY={2} marginX={1} />}
              label={project?.category?.name}
              sx={{ mt: 1, '& .MuiChip-label': { pl: 0 } }}
            />
          ) : null
        }
      />
      <ModalContainer.Content>
        {project?.archive_date ? (
          <ProjectArchived project={project} callbacks={{ toggleUnarchiveSuccess: () => closeModal() }} />
        ) : (
          <Stack direction="column" p={2} spacing={2}>
            {parts
              ?.filter((x) => x.name === 'Service Address')
              ?.map((part) => (
                <PartTypes
                  key={part.number}
                  callbacks={{ toggleSetValue: (value, error) => setValue(part, value, error) }}
                  part={{ detail: part, hideLabel: true }}
                />
              ))}
            <TextField
              id="outlined-basic"
              label="Project Name"
              required
              value={display_name}
              variant="outlined"
              fullWidth
              onChange={(e) => {
                setState({ projectUpdated: true })
                setEntityState({ display_name: e.target.value })
              }}
            />
            {(!['Agent', 'Client_Admin', 'Assistant'].some(role => currentUser?.roles?.includes(role)) && office) || (!['Agent', 'Client_Admin', 'Assistant'].some(role => currentUser?.roles?.includes(role)) && project?.owner) ? (
              <Autocomplete
                name="Owner"
                fullWidth
                groupBy={(option) => option.roles[0] || 'User'}
                options={Object.values(users)
                  .sort((a, b) => {
                    if (a.roles[0] < b.roles[0]) return -1
                    if (a.roles[0] > b.roles[0]) return 1

                    return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1
                  })
                  .filter((x) => !x?.deleted_at)
                  .filter((x) => !collaborators?.includes(x.id))
                  .filter((x) => isUserInOfficeScope(x, [owner]))}
                getOptionLabel={(option) => {
                  const name = option.name || ''
                  return option?.deleted_at ? `${name} (Deleted)` : name
                }}
                onChange={(_, value) => {
                  setEntityState({ owner: value?.id || '' })
                }}
                renderInput={(params) => <TextField {...params} name="owner" label="Owner" helperText={errors['owner']} required />}
                value={Object.values(users).find((x) => x.id === owner) || null}
              />
            ) : (
              <TextField id="outlined-basic" label="Owner" disabled value={ownerUser?.name || 'No Owner'} variant="outlined" fullWidth />
            )}
            <Autocomplete
              name="Collaborators"
              multiple
              autoComplete
              fullWidth
              groupBy={(option) => option.roles[0] || 'User'}
              options={Object.values(users)
                .sort((a, b) => {
                  if (a.roles[0] < b.roles[0]) return -1
                  if (a.roles[0] > b.roles[0]) return 1

                  return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1
                })
                .filter((x) => x.id !== owner)
                .filter((x) => !x?.deleted_at)
                .filter((x) => isUserInOfficeScope(x, collaborators))}
              getOptionLabel={(option) => {
                const name = option.name || ''
                return option?.deleted_at ? `${name} (Deleted)` : name
              }}
              includeInputInList
              autoSelect
              onChange={(_, value) => {
                const userIds = value.map((x) => x.id)
                setEntityState({ collaborators: userIds })
              }}
              renderInput={(params) => (
                <TextField {...params} name="collaborators" label="Collaborators" helperText={errors['collaborators']} />
              )}
              value={
                Object.values(users)
                  .filter((x) => collaborators.includes(x.id))
                  .sort((a, b) => (a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1)) || null
              }
            />
            {project === undefined && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipChecked}
                    onChange={(e) => setState({ skipChecked: e.target.checked })}
                    size="small"
                    style={{ paddingLeft: 0 }}
                  />
                }
                label="Skip out details for later"
                labelPlacement="end"
                value="end"
              />
            )}
            {!(!project?.id && project?.category) && (
              <Stack
                direction="column"
                spacing={2}
                p={1}
                sx={
                  disableFields
                    ? {
                      background: theme.palette.background.paper,
                      opacity: '0.3',
                      pointerEvents: 'none',
                    }
                    : {}
                }
              >
                {templateLoading ? (
                  <Skeleton variant="rectangular" animation="wave" height="50vh" />
                ) : (
                  parts
                    ?.filter((x) => x.name !== 'Service Address')
                    ?.map((part) => (
                      <PartTypes
                        key={part.number}
                        callbacks={{ toggleSetValue: (value, error) => setValue(part, value, error) }}
                        part={{ detail: part }}
                      />
                    ))
                )}
              </Stack>
            )}
            {entityState?.id && (
              <Stack direction="column" spacing={2}>
                <Typography variant="h5">Other Options</Typography>
                <Button
                  loading={updating}
                  disabled={!entityState?.id}
                  variant="contained"
                  disableElevation
                  fullWidth
                  onClick={() => setArchive(true)}
                >
                  Archive
                </Button>
              </Stack>
            )}
          </Stack>
        )}
        <Dialog
          open={openArchive}
          onClose={() => setArchive(false)}
          sx={{
            '& .MuiDialogTitle-root': { fontSize: '1.25rem!important' },
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ borderBottom: 1, marginBottom: 1, borderColor: theme.palette.primary.light }}>
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>
                Confirm Archive
              </Typography>
              <CloseOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => setArchive(false)} />
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <ArchiveOutlined sx={{ fontSize: 80 }} />
            <Typography variant="h4" sx={{ py: 1 }}>
              Are you sure?
            </Typography>
            <DialogContentText>You are about to archive this project.</DialogContentText>
            <DialogContentText>Click "Confirm" to proceed.</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchive(false)}>Cancel</Button>
            <Button loading={updating} variant="contained" disableElevation onClick={() => archive()}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ModalContainer.Content>
      <ModalContainer.Footer>
        {project?.archive_date ? null : (
          <Button
            disableElevation
            disabled={
              !saveEnabled ||
              templateLoading ||
              !!errors.length ||
              !(parts?.find((x) => x.name === 'Service Address')?.value?.address || null)
            }
            loading={creating || updating}
            fullWidth
            onClick={() => save(project?.id)}
            variant="contained"
          >
            {project?.id ? 'Save' : 'Create'}
          </Button>
        )}
        <Button onClick={closeModal} variant="outlined" fullWidth style={{ marginTop: 6 }}>
          Cancel
        </Button>
      </ModalContainer.Footer>
    </ModalContainer>
  )
}

ProjectModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = (props) => (
  <ModalContainer.RenderController {...props}>
    <ProjectModal {...props} />
  </ModalContainer.RenderController>
)

export default LazyLoadedModal
